import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const SubscriptionLogs = ({ idSuscripcion }) => {
    const { token } = useToken();
    const { showErrorToast } = useToast();
    const [logsData, setLogsData] = useState([]);
    const [cargandoLogs, setCargandoLogs] = useState(true);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [cargandoMas, setCargandoMas] = useState(false);

    /**
     * Formatea una fecha al formato [DD Mes YYYY a las HH:MM]
     */
    const formatearFecha = (fecha) => {
        if (!fecha) return '';
        const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        const date = new Date(fecha);
        const dia = date.getDate();
        const mes = meses[date.getMonth()];
        const anio = date.getFullYear();
        const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        return `${dia} ${mes} ${anio} a las ${time}`;
    };

    useEffect(() => {
        if (idSuscripcion) {
            setPage(1);
            setHasMore(true);
            setLogsData([]);
            obtenerLogs(1);
        }
    }, [idSuscripcion]);

    const obtenerLogs = async (pageNumber = 1) => {
        if (pageNumber === 1) {
            setCargandoLogs(true);
        } else {
            setCargandoMas(true);
        }
        
        try {
            const response = await fetch(`/api/Suscripciones/ObtenerLogs?idSuscripcion=${idSuscripcion}&PageNumber=${pageNumber}&PageSize=5`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    let parsed = [];
                    // Asegurarnos de que detalles es string para parsearlo, o si ya es objeto/array usarlo directo
                    if (typeof data.detalles === 'string') {
                        parsed = JSON.parse(data.detalles);
                    } else if (Array.isArray(data.detalles)) {
                        parsed = data.detalles;
                    }
                    
                    if (parsed.length < 5) {
                        setHasMore(false);
                    }

                    if (pageNumber === 1) {
                        setLogsData(parsed);
                    } else {
                        setLogsData(prev => [...prev, ...parsed]);
                    }
                } else {
                    showErrorToast(data.vMessage);
                }
            } else {
                showErrorToast('Error de red al obtener logs');
            }
        } catch (error) {
            console.error('Error al obtener logs:', error);
            showErrorToast('Error al obtener logs');
        } finally {
            setCargandoLogs(false);
            setCargandoMas(false);
        }
    };

    const handleScroll = (e) => {
        const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 50;
        if (bottom && hasMore && !cargandoLogs && !cargandoMas) {
            const nextPage = page + 1;
            setPage(nextPage);
            obtenerLogs(nextPage);
        }
    };

    /**
     * Mapea la acción técnica de base de datos a una etiqueta amigable con icono y colores semánticos
     */
    const getAccionConfig = (vAccion) => {
        const accion = (vAccion || '').toUpperCase().trim();

        if (accion === 'CANCEL_AT_PERIOD_END' || accion === 'CANCELACION_PROGRAMADA' || accion === 'CANCELACIÓN PROGRAMADA') {
            return {
                label: 'CANCELACIÓN PROGRAMADA',
                borderColor: '#f59e0b',
                badgeStyle: { color: '#b45309', borderColor: '#f59e0b', backgroundColor: '#fffbeb' },
                icon: 'clock'
            };
        }

        if (accion === 'SUBSCRIPTION_DELETED' || accion === 'IMMEDIATE_CANCEL_STRIPE' || accion === 'CANCELADA' || accion === 'CANCELACIÓN DEFINITIVA') {
            return {
                label: 'CANCELACIÓN DEFINITIVA',
                borderColor: '#ef4444',
                badgeStyle: { color: '#b91c1c', borderColor: '#ef4444', backgroundColor: '#fef2f2' },
                icon: 'times-circle'
            };
        }

        if (accion.includes('REACTIVATE') || accion.includes('REACTIVADA')) {
            return {
                label: 'REACTIVADA',
                borderColor: '#10b981',
                badgeStyle: { color: '#047857', borderColor: '#10b981', backgroundColor: '#ecfdf5' },
                icon: 'check-circle'
            };
        }

        return {
            label: vAccion,
            borderColor: '#3182ce',
            badgeStyle: { color: '#2b6cb0', borderColor: '#3182ce' },
            icon: 'sync-alt'
        };
    };

    const formatDetalle = (texto, usuario) => {
        if (!texto) return texto;

        // Desaparecer "Motivo: ..." o ". Motivo: ..." ya que no se pide motivo al cancelar
        let cleanTexto = texto.replace(/\.?\s*Motivo\s*:\s*.*$/i, '').trim();

        // Escapar el nombre de usuario por si tiene caracteres especiales
        const safeUser = usuario ? usuario.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : '';

        // Expresión regular para encontrar: texto entre **, el usuario, el #ID, y los precios entre paréntesis
        const pattern = `(\\*\\*.*?\\*\\*|${safeUser}|#\\d+|\\(\\$\\d+(?:\\.\\d+)?\\))`;
        const regex = new RegExp(pattern, 'gi');

        const parts = cleanTexto.split(regex);

        return parts.map((part, i) => {
            if (!part) return null;

            // Creamos una expresión exacta para probar la parte dividida
            const testRegex = new RegExp(`^(\\*\\*.*?\\*\\*|${safeUser}|#\\d+|\\(\\$\\d+(?:\\.\\d+)?\\))$`, 'i');
            
            if (testRegex.test(part)) {
                let displayPart = part;
                // Si la parte está envuelta en **, le quitamos los asteriscos para mostrarla
                if (part.startsWith('**') && part.endsWith('**')) {
                    displayPart = part.slice(2, -2);
                }
                return <strong key={i} className="fw-bold">{displayPart}</strong>;
            }
            return part;
        });
    };

    return (
        <Card className="border-0 shadow-sm ">
            <Card.Header className="bg-transparent border-bottom-0 pt-3 pb-0">
                <h5 className="mb-0 fw-bold">
                    <FontAwesomeIcon icon="history" className="me-2 text-primary " />
                    Historial de Actividad Suscripciones
                </h5>
            </Card.Header>
            <Card.Body>
                {cargandoLogs ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                        <div className="mt-2 text-muted">Cargando historial...</div>
                    </div>
                ) : logsData.length > 0 ? (
                    <div 
                        className="d-flex flex-column gap-3 pe-2" 
                        style={{ maxHeight: '400px', overflowY: 'auto' }}
                        onScroll={handleScroll}
                    >
                        {logsData.map((log, idx) => {
                            const accionConfig = getAccionConfig(log.vAccion);
                            return (
                                <Card 
                                    key={idx} 
                                    className="border-0 shadow-sm" 
                                    style={{ 
                                        borderLeft: `4px solid ${accionConfig.borderColor}`, 
                                        borderRadius: '8px' 
                                    }}
                                >
                                    <Card.Body className="p-3">
                                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                            <span 
                                                className="badge px-2 py-1 shadow-sm"
                                                style={{
                                                    ...accionConfig.badgeStyle,
                                                    borderWidth: '1px',
                                                    borderStyle: 'solid'
                                                }}
                                            >
                                                <FontAwesomeIcon icon={accionConfig.icon} className="me-1" />
                                                {accionConfig.label}
                                            </span>
                                            <small className="text-muted fw-semibold border-start border-2 ps-2">
                                                <FontAwesomeIcon icon="user" className="me-1" />
                                                {log.vCreatedUser}
                                            </small>
                                            <small className="text-muted fw-semibold border-start border-2 ps-2">
                                                <FontAwesomeIcon icon="clock" className="me-1" />
                                                {formatearFecha(log.dCreatedDate)}
                                            </small>
                                        </div>
                                        <p className="mb-0 text-secondary" style={{ fontSize: '0.95rem' }}>
                                            {formatDetalle(log.vDetalle, log.vCreatedUser)}
                                        </p>
                                    </Card.Body>
                                </Card>
                            );
                        })}
                        {cargandoMas && (
                            <div className="text-center py-3">
                                <Spinner animation="border" variant="primary" size="sm" />
                                <span className="ms-2 text-muted" style={{ fontSize: '0.85rem' }}>Cargando más...</span>
                            </div>
                        )}
                    </div>
                ) : (
                    <Alert variant="light" className="text-center border shadow-sm mb-0">
                        <span className="text-muted">No hay movimientos registrados para esta suscripción.</span>
                    </Alert>
                )}
            </Card.Body>
        </Card>
    );
};

SubscriptionLogs.propTypes = {
    idSuscripcion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default SubscriptionLogs;
